<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  show: Boolean,
  token: String,
  tokenName: String,
  tokenType: {
    type: String,
    default: 'api', // 'api' or 'web'
    validator: (value) => ['api', 'web'].includes(value)
  },
  expiresAt: String,
  abilities: {
    type: Array,
    default: () => []
  },
  onClose: Function
})

const emit = defineEmits(['close', 'copy'])

const isCopied = ref(false)
const copyTimeout = ref(null)
const isDarkMode = ref(false)

// Check for dark mode preference
onMounted(() => {
  isDarkMode.value = window.matchMedia('(prefers-color-scheme: dark)').matches
})

onUnmounted(() => {
  if (copyTimeout.value) {
    clearTimeout(copyTimeout.value)
  }
})

const copyToken = async () => {
  try {
    await navigator.clipboard.writeText(props.token)
    isCopied.value = true
    
    // Clear previous timeout
    if (copyTimeout.value) {
      clearTimeout(copyTimeout.value)
    }
    
    // Reset copied state after 2 seconds
    copyTimeout.value = setTimeout(() => {
      isCopied.value = false
    }, 2000)
    
    emit('copy', props.token)
  } catch (err) {
    console.error('Failed to copy token:', err)
  }
}

const closePopup = () => {
  emit('close')
  if (props.onClose) {
    props.onClose()
  }
}

const getTokenTypeInfo = () => {
  if (props.tokenType === 'web') {
    return {
      icon: 'fas fa-globe',
      color: 'blue',
      title: 'Web Login Token',
      description: 'Short-lived token for web application access',
      bgColor: 'bg-blue-500',
      textColor: 'text-blue-500'
    }
  } else {
    return {
      icon: 'fas fa-code',
      color: 'green',
      title: 'API Integration Token',
      description: 'Long-lived token for external API access',
      bgColor: 'bg-green-500',
      textColor: 'text-green-500'
    }
  }
}

const formatExpiration = () => {
  if (!props.expiresAt) return 'Never expires'
  
  const now = new Date()
  const expiryDate = new Date(props.expiresAt)
  const diffTime = expiryDate - now
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  
  if (diffTime < 0) {
    return 'Expired'
  } else if (diffDays === 1) {
    return 'Expires tomorrow'
  } else if (diffDays <= 7) {
    return `Expires in ${diffDays} days`
  } else if (diffDays <= 30) {
    const weeks = Math.ceil(diffDays / 7)
    return `Expires in ${weeks} week${weeks > 1 ? 's' : ''}`
  } else {
    const months = Math.ceil(diffDays / 30)
    return `Expires in ${months} month${months > 1 ? 's' : ''}`
  }
}

const getExpirationColor = () => {
  if (!props.expiresAt) return 'text-gray-500'
  
  const now = new Date()
  const expiryDate = new Date(props.expiresAt)
  const diffTime = expiryDate - now
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  
  if (diffTime < 0) return 'text-red-500'
  if (diffDays <= 1) return 'text-red-500'
  if (diffDays <= 7) return 'text-yellow-500'
  if (diffDays <= 30) return 'text-orange-500'
  return 'text-green-500'
}

const tokenTypeInfo = getTokenTypeInfo()
</script>

<template>
  <Transition
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="transform scale-95 opacity-0"
    enter-to-class="transform scale-100 opacity-100"
    leave-active-class="transition duration-200 ease-in"
    leave-from-class="transform scale-100 opacity-100"
    leave-to-class="transform scale-95 opacity-0"
  >
    <div v-if="show" 
         class="fixed inset-0 z-[9999] flex items-center justify-center p-4"
         :class="isDarkMode ? 'bg-black/80' : 'bg-black/50'"
         @click.self="closePopup"
    >
      <!-- Backdrop blur -->
      <div class="absolute inset-0 backdrop-blur-sm"></div>
      
      <!-- Modal -->
      <div class="relative w-full max-w-md transform transition-all"
           :class="isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'"
      >
        <!-- Header -->
        <div class="flex items-center justify-between p-6 border-b"
             :class="isDarkMode ? 'border-gray-700' : 'border-gray-200'"
        >
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-full flex items-center justify-center"
                 :class="isDarkMode ? 'bg-gray-800' : 'bg-gray-100'"
            >
              <i :class="[tokenTypeInfo.icon, tokenTypeInfo.textColor, 'text-lg']"></i>
            </div>
            <div>
              <h3 class="text-lg font-semibold">{{ tokenTypeInfo.title }}</h3>
              <p class="text-sm opacity-70">{{ tokenTypeInfo.description }}</p>
            </div>
          </div>
          <button @click="closePopup" 
                  class="p-2 rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Content -->
        <div class="p-6 space-y-6">
          <!-- Token Name -->
          <div v-if="tokenName">
            <label class="block text-sm font-medium mb-2 opacity-70">Token Name</label>
            <div class="text-lg font-semibold">{{ tokenName }}</div>
          </div>

          <!-- Token Display -->
          <div>
            <label class="block text-sm font-medium mb-3 opacity-70">Your Token</label>
            <div class="relative">
              <div class="font-mono text-sm p-4 rounded-lg border-2 break-all"
                   :class="isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'"
              >
                {{ token }}
              </div>
              <button 
                @click="copyToken"
                class="absolute top-2 right-2 p-2 rounded-lg transition-all duration-200"
                :class="isCopied 
                  ? 'bg-green-500 text-white' 
                  : isDarkMode 
                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-600'"
              >
                <i :class="isCopied ? 'fas fa-check' : 'fas fa-copy'"></i>
              </button>
            </div>
            <p class="text-xs mt-2 opacity-60">
              {{ isCopied ? 'Token copied to clipboard!' : 'Click the copy button to copy the token' }}
            </p>
          </div>

          <!-- Token Information -->
          <div class="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span class="font-medium opacity-70">Type:</span>
              <div class="flex items-center gap-2 mt-1">
                <i :class="[tokenTypeInfo.icon, tokenTypeInfo.textColor]"></i>
                <span>{{ tokenTypeInfo.title }}</span>
              </div>
            </div>
            <div>
              <span class="font-medium opacity-70">Expires:</span>
              <div class="mt-1" :class="getExpirationColor()">
                {{ formatExpiration() }}
              </div>
            </div>
          </div>

          <!-- Abilities -->
          <div v-if="abilities && abilities.length > 0">
            <label class="block text-sm font-medium mb-2 opacity-70">Permissions</label>
            <div class="flex flex-wrap gap-2">
              <span v-for="ability in abilities" :key="ability"
                    class="px-3 py-1 text-xs rounded-full font-medium"
                    :class="ability === '*' 
                      ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                      : isDarkMode 
                        ? 'bg-gray-700 text-gray-300' 
                        : 'bg-gray-100 text-gray-700'"
              >
                {{ ability === '*' ? 'All Permissions' : ability }}
              </span>
            </div>
          </div>

          <!-- Security Notice -->
          <div class="p-4 rounded-lg border"
               :class="isDarkMode ? 'bg-yellow-900/20 border-yellow-700 text-yellow-200' : 'bg-yellow-50 border-yellow-200 text-yellow-800'"
          >
            <div class="flex items-start gap-3">
              <i class="fas fa-shield-alt mt-0.5"></i>
              <div class="text-sm">
                <div class="font-medium mb-1">Security Notice</div>
                <div class="opacity-80">
                  {{ tokenType === 'web' 
                    ? 'This token will be automatically revoked when you log out or after 24 hours.'
                    : 'Keep this token secure. It provides long-term access to your account.' }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex justify-end gap-3 p-6 border-t"
             :class="isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'"
        >
          <button 
            @click="closePopup"
            class="px-4 py-2 rounded-lg transition-colors"
            :class="isDarkMode 
              ? 'bg-gray-700 hover:bg-gray-600 text-white' 
              : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-300'"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
/* Custom scrollbar for dark mode */
.dark ::-webkit-scrollbar {
  width: 6px;
}

.dark ::-webkit-scrollbar-track {
  background: #374151;
}

.dark ::-webkit-scrollbar-thumb {
  background: #6b7280;
  border-radius: 3px;
}

.dark ::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style> 